import { useEffect, useMemo } from 'react';
import { Button, Select } from 'antd';
import styled from '@emotion/styled';
import { FieldArray, useFormikContext } from 'formik';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import TimeGroup from './TimeGroup';
import useSmartField from 'hooks/useSmartField';
import { useTranslate } from '@tolgee/react';
import FormField from 'components/FormField';

export type DayOfWeeks = (
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY'
)[];

type WeeKGroupProps = {
  fieldNamePrefix: string;
};

function newRange(defaultValue: boolean = false) {
  let dayOfWeeks: DayOfWeeks = [];
  if (defaultValue) {
    dayOfWeeks = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  }
  return { dayOfWeeks, timeRanges: [] };
}

function WeeKGroup(props: WeeKGroupProps) {
  const { fieldNamePrefix } = props;
  const { field: weekRepeatField, helpers: weekRepeatHelpers } = useSmartField(fieldNamePrefix);
  useEffect(() => {
    if (weekRepeatField?.value?.length === 0) {
      weekRepeatHelpers.setValue([newRange(true)]);
    }
  }, [weekRepeatField, weekRepeatHelpers]);
  const { t } = useTranslate();
  const { setFieldValue } = useFormikContext();

  return (
    <Container>
      <FieldArray name={fieldNamePrefix}>
        {(arrayHelpers) => {
          return (
            <>
              {weekRepeatField?.value?.map((each, index) => {
                return (
                  <Item key={index}>
                    <div className="flex h-[40px] px-[16px] py-[10px] border-0 border-b-[var(--fett-border-default)] border-b border-solid text-[var(--fett-text-primary)]">
                      {t('multiTimeSelect.生效时间段')}
                      {index + 1}
                    </div>
                    <Cancel
                      onClick={() => {
                        if (weekRepeatField?.value?.length > 1) {
                          arrayHelpers.remove(index);
                        }
                      }}
                    />
                    <div className="p-[16px]">
                      <FormField
                        name={`${fieldNamePrefix}.${index}.dayOfWeeks`}
                        label={t('multiTimeSelect.每周')}
                        required
                        validate={(value) => {
                          if (value?.length === 0) {
                            return t('multiTimeSelect.请选择星期');
                          }
                        }}
                      >
                        {({ field }) => {
                          return (
                            <div>
                              <SelectWeekDay
                                value={field.value}
                                onChange={(value) => {
                                  field.onChange(value);
                                  weekRepeatField?.value?.forEach((each, i) => {
                                    if (i !== index) {
                                      setFieldValue(
                                        `${fieldNamePrefix}.${i}.dayOfWeeks`,
                                        each.dayOfWeeks.filter((each) => !value.includes(each)),
                                      );
                                    }
                                  });
                                }}
                              />
                            </div>
                          );
                        }}
                      </FormField>
                      <TimeGroup fieldNamePrefix={`${fieldNamePrefix}.${index}.timeRanges`} />
                    </div>
                  </Item>
                );
              })}
              {weekRepeatField?.value?.length < 4 && (
                <Button
                  type="link"
                  style={{ padding: '0' }}
                  onClick={() => {
                    if (weekRepeatField?.value?.length < 4) {
                      arrayHelpers.push(newRange());
                    }
                  }}
                  icon={<PlusOutlined />}
                >
                  {t('multiTimeSelect.新增时间段')}
                </Button>
              )}
            </>
          );
        }}
      </FieldArray>
    </Container>
  );
}

type SelectWeekDayProps = {
  value: DayOfWeeks;
  onChange: (value: DayOfWeeks) => void;
};

function SelectWeekDay(props: SelectWeekDayProps) {
  const { value, onChange } = props;
  const { t } = useTranslate();
  const weekDayOptions = useMemo(() => {
    return [
      { label: t('multiTimeSelect.周一'), value: 'MONDAY' },
      { label: t('multiTimeSelect.周二'), value: 'TUESDAY' },
      { label: t('multiTimeSelect.周三'), value: 'WEDNESDAY' },
      { label: t('multiTimeSelect.周四'), value: 'THURSDAY' },
      { label: t('multiTimeSelect.周五'), value: 'FRIDAY' },
      { label: t('multiTimeSelect.周六'), value: 'SATURDAY' },
      { label: t('multiTimeSelect.周日'), value: 'SUNDAY' },
    ];
  }, [t]);

  return (
    <Select
      mode="multiple"
      style={{ marginBottom: '8px', width: '100%' }}
      placeholder={t('multiTimeSelect.请选择星期')}
      value={value}
      onChange={onChange}
    >
      {weekDayOptions.map((each) => {
        return (
          <Select.Option key={each.value} value={each.value}>
            {each.label}
          </Select.Option>
        );
      })}
    </Select>
  );
}

const Container = styled.div``;

const Item = styled.div`
  position: relative;
  margin-bottom: 8px;
  background: var(--fett-bg-panel);
`;

const Cancel = styled(DeleteOutlined)`
  position: absolute;
  top: 8px;
  right: 10px;
  width: 24px;
  height: 24px;
  background: var(--fett-bg-panel);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--fett-text-tertiary);
`;

export default WeeKGroup;
